import db from '../../../../../../../utils/datastore/indexed-db';
const sToMins = (s: number) => Math.floor(s / 60);

enum ActionSource {
	Landing = 'landing',
	Home = 'home',
	Agenda = 'agenda',
	CustomPage = 'custom_page',
	Directory = 'directory',
	Profile = 'profile',
	Session = 'session',
	BreakoutRoom = 'breakout_room',
	Fireside = 'fireside',
	SelfProfile = 'self_profile',
	Leaderboard = 'leaderboard',
}

enum ActionEvent {
	PageView = 'pageview',
	Click = 'click',
	Download = 'download',
	End = 'end',
	Milestone = 'milestone',
	Pause = 'pause',
	Play = 'play',
	Start = 'start',
	View = 'view',
	WatchedMinute = 'watched-minute'
}

type InitInput = {
	event_id: number;
	session_id: number;
	channel_id: number;
	bl_profile: number;
	is_course: boolean;
	is_live: boolean;
	course_uuid: string | undefined;
	requirement_id: number | undefined;
}

type ActionOutput = {
	action: ActionEvent.WatchedMinute;
	event_id: number;
	source_type: ActionSource.Session,
	source_id: number;
	channel_id: number;
	bl_profile: number;
	watched_seconds?: number;
	live?: boolean;
	course_uuid?: string;
	requirement_id?: number;
};

const track = (origin: string, action: ActionOutput, blProfileUserToken: string) => {
	fetch(`${origin}/leaderboard-tracked-action`, {
		method: 'POST',
		headers: {
			'bl-profile-token': blProfileUserToken,
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(action)
	}).catch(console.error);
};

type TimingLive = {
	isLive: true;
	startTime: number;
	endTime: number;
	duration?: never;
}

type TimingRecorded = {
	isLive: false;
	startTime?: never;
	endTime?: never;
	duration: number;
}

type Timing = TimingLive | TimingRecorded;

const between = (start: number, target: number, end: number) => {
	return target >= start && target < end;
};

class MessageTracker {
	private minutesWatched: number[] = [];
	private initializing = false;
	private uuid: string | undefined;
	private action: ActionOutput | undefined;
	private timing: Timing | undefined;
	private origin: string | undefined;
	private blProfileUserToken: string | undefined;
	public enabled = true;

	init = async (
		sessionUuid: string,
		action: InitInput,
		timing: Timing,
		origin: string,
		blProfileUserToken: string,
	) => {
		this.timing = timing;
		this.origin = origin;

		// set up 
		this.action = {
			event_id: action.event_id,
			source_type: ActionSource.Session,
			source_id: action.session_id,
			channel_id: action.channel_id,
			bl_profile: action.bl_profile,
			action: ActionEvent.WatchedMinute,
		};

		this.initializing = true;
		this.uuid = sessionUuid;
		this.blProfileUserToken = blProfileUserToken;

		// get last time from previous session
		const storedMinutesWatched = await db.getItem<number[]>(`session-video`, sessionUuid);

		// onProgress handler from player receives update of current played second each second. 
		this.minutesWatched = storedMinutesWatched ?? [0];
		this.initializing = false;
		this.enabled = true;
		courseTracker.enabled = false;
	};

	canProgress = (currentMinute: number): boolean => {
		if (!this.timing) return false;

		if (this.timing.isLive) {
			if (between(this.timing.startTime, Date.now(), this.timing.endTime)) {
				return !this.minutesWatched.includes(currentMinute);
			}

			return false;
		} else {
			// duration is in seconds
			return currentMinute * 60 < this.timing.duration && !this.minutesWatched.includes(currentMinute);
		}
	};

	getMinuteWatched = (second?: number) => {
		if (!this.timing) return 0;

		if (this.timing.isLive) {
			const now = Date.now();
			// number of minutes as integer from the broadcast start timestamp until now - cannot be negative
			return Math.floor(Math.max((now - this.timing.startTime) / (1000 * 60), 0));

		} else if (second) {
			return sToMins(second);
		}
	};

	onProgress = (second?: number, end?: boolean) => {
		if (this.initializing || !this.origin) return;

		const currentMinute = this.getMinuteWatched(second);

		if (!currentMinute) return;
		if (this.canProgress(currentMinute) || (end && second)) {
			this.minutesWatched = Array.from(new Set([...this.minutesWatched, currentMinute])).sort();

			db.putItem({
				uuid: this.uuid as string,
				type: `session-video`,
				data: this.minutesWatched,
				category: 'tracking'
			}).catch(console.error);

			track(this.origin, this.action as ActionOutput, this.blProfileUserToken as string);
		}
	};
}

const toMinute = (ms: number) => Math.floor(ms / 1000 / 60);
const toSecond = (ms: number) => Math.floor(ms / 1000);

const canLog = globalThis.location?.search?.includes('log-tracking');

const debugLog = (...args: unknown[]) => {
	if (canLog) {
		console.log(...args);
	}
};

class CourseWatchedMinutes {
	public lastWatchedTimestamp: number | undefined;
	public lastSentTimestamp: number | undefined;
	private uuid: string | undefined;
	private action: ActionOutput | undefined;
	private origin: string | undefined;
	private blProfileUserToken: string | undefined;
	private is_live: boolean | undefined;
	private course_uuid: string | undefined;
	private requirement_id: number | undefined;
	public enabled = false;
	private _playing = false;
	public backgrounded = false;

	// user stopped the video so the timer should not progress
	// we need to reset the last sent timestamp when they resume
	// so that we don't grant them minutes they didn't watch
	public set playing(value: boolean) {
		debugLog('playing', value);

		if (value) {
			this.lastSentTimestamp = Date.now();
		}

		this._playing = value;
	}

	init = (
		sessionUuid: string,
		action: InitInput,
		origin: string,
		blProfileUserToken: string,
		isLive: boolean,
		courseUuid: string,
		requirementId: number,
		initialSecondsWatched = 0
	) => {
		this.origin = origin;
		this.action = {
			event_id: action.event_id,
			source_type: ActionSource.Session,
			source_id: action.session_id,
			channel_id: action.channel_id,
			bl_profile: action.bl_profile,
			action: ActionEvent.WatchedMinute,
			live: action.is_live,
			course_uuid: action.course_uuid
		};

		this.uuid = sessionUuid;
		this.blProfileUserToken = blProfileUserToken;
		this.is_live = isLive;
		this.course_uuid = courseUuid;
		this.requirement_id = requirementId;
		this.lastWatchedTimestamp = Date.now();
		this.enabled = true;
		tracker.enabled = false;
		this._playing = true;

		if (initialSecondsWatched) {
			this.lastSentTimestamp = Date.now() - (initialSecondsWatched * 1000);
		} else {
			this.lastSentTimestamp = Date.now();
		}

		debugLog('init', this);

		// storing minutes watched only needed when we add in on-demand course videos
		// const storedMinutesWatched = await db.getItem<number[]>(`course-video`, sessionUuid);
	};

	canSend = () => {
		if (!this.lastWatchedTimestamp || !this.lastSentTimestamp) return false;

		// can send update if there has been a minute since the last update
		return (this.lastSentTimestamp + 60000 < this.lastWatchedTimestamp);
	};

	watchedSeconds = () => {
		if (!this.lastWatchedTimestamp || !this.lastSentTimestamp) return 0;

		const lastSentSecond = toSecond(this.lastSentTimestamp);
		const currentSecond = toSecond(this.lastWatchedTimestamp);
		return currentSecond - lastSentSecond;
	};

	flushRemainingSeconds = () => {
		if (!this.origin) return;

		const watched_seconds = this.watchedSeconds();

		if (watched_seconds < 10) return;

		track(this.origin, {
			...this.action,
			watched_seconds,
			live: this.is_live,
			course_uuid: this.course_uuid,
			requirement_id: this.requirement_id
		} as ActionOutput, this.blProfileUserToken as string);

		this.lastSentTimestamp = Date.now();

		debugLog('Sent course tracking', this.action);

		self.postMessage({
			session_id: (this.action as ActionOutput).source_id,
			watched_seconds
		});
	};

	onEnded = () => {
		debugLog('ended', this);
		this.flushRemainingSeconds();
		this.playing = false;
	};

	onProgress = () => {
		if (!this.origin || !this._playing || this.backgrounded) return;

		this.lastWatchedTimestamp = Date.now();
		const watched_seconds = this.watchedSeconds();

		// commented out until on-demand course videos are added
		// db.putItem({
		// 	uuid: this.uuid as string,
		// 	type: `course-video`,
		// 	data: watched_seconds,
		// 	category: 'tracking'
		// }).catch(console.error);
		debugLog('watched_seconds', watched_seconds);

		if (this.canSend()) {
			track(this.origin, {
				...this.action,
				watched_seconds,
				live: this.is_live,
				course_uuid: this.course_uuid,
				requirement_id: this.requirement_id
			} as ActionOutput, this.blProfileUserToken as string);

			this.lastSentTimestamp = Date.now();

			debugLog('Sent course tracking', this.action);

			self.postMessage({
				session_id: (this.action as ActionOutput).source_id,
				watched_seconds
			});
		}
	};
}

const tracker = new MessageTracker();
const courseTracker = new CourseWatchedMinutes();

export type TrackMessageInit = {
	type: 'init';
	payload: {
		timing: Timing;
		sessionUuid: string;
		action: InitInput;
		trackingOrigin: string;
		blProfileUserToken: string;
		is_live?: boolean;
		is_course?: boolean;
		course_uuid?: string;
		requirement_id?: number;
		watched_seconds?: number;
	};
};

export type TrackMessagePing = {
	type: 'progress' | 'ended' | 'watching' | 'playing' | 'stopped' | 'idled' | 'backgrounded' | 'foregrounded',
	payload?: {
		second: number;
	}
}

const isString = (str?: unknown): str is string => typeof str === 'string';
const isNumber = (num?: unknown): num is number => typeof num === 'number';

// received message from main thread
const handleTrackMessage = ({ data }: MessageEvent<TrackMessageInit | TrackMessagePing>) => {
	debugLog('Received message', data);
	switch (data.type) {
		case 'init': {
			if (!data.payload.is_course) {
				tracker.init(
					data.payload.sessionUuid,
					data.payload.action,
					data.payload.timing,
					data.payload.trackingOrigin,
					data.payload.blProfileUserToken,
				);
			} else if (isString(data.payload.course_uuid) && isNumber(data.payload.requirement_id)) {
				courseTracker.init(
					data.payload.sessionUuid,
					data.payload.action,
					data.payload.trackingOrigin,
					data.payload.blProfileUserToken,
					!!data.payload.is_live,
					data.payload.course_uuid,
					data.payload.requirement_id,
					data.payload.watched_seconds
				);
			} else {
				console.error('Invalid course tracking init payload');
			}
			break;
		}

		case 'foregrounded': {
			if (courseTracker.enabled && courseTracker.backgrounded) {
				// assumes that when the page was backgrounded any remaining seconds would have been sent
				// the player might still be playing but the tracker is not sending updates
				// as soon as the app is foregrounded we want to reset the clock and start counting afresh
				courseTracker.lastWatchedTimestamp = Date.now();
				courseTracker.lastSentTimestamp = Date.now();
				courseTracker.backgrounded = false;
			}

			break;
		}
		case 'watching': {
			if (courseTracker.enabled) {
				courseTracker.onProgress();
			}

			break;
		}

		case 'playing': {
			if (courseTracker.enabled) {
				courseTracker.playing = true;
			}

			break;
		}

		case 'stopped': {
			if (courseTracker.enabled) {
				courseTracker.playing = false;
				courseTracker.onEnded();
			}

			break;
		}

		case 'idled': {
			if (courseTracker.enabled) {
				courseTracker.playing = false;
			}

			break;
		}

		case 'progress': {
			if (tracker.enabled) {
				tracker.onProgress(data.payload?.second);
			}

			break;
		}

		case 'backgrounded': {
			if (courseTracker.enabled) {
				// user has backgrounded the page. Send the remaining clock seconds to the server 
				// and stop bothering with time updates. 
				courseTracker.flushRemainingSeconds();
				courseTracker.backgrounded = true;
			}

			break;
		}

		case 'ended': {
			if (courseTracker.enabled) {
				courseTracker.playing = false;
				courseTracker.onEnded();
			}

			break;
		}

		default: {
			debugLog(`Unmapped payload received: ${JSON.stringify(data)}`);
		}
	}
};

self.onmessage = handleTrackMessage;